import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiResponse } from '@domain/api';
import { BulkUpdateRatesRequest, CreatePriceRequest, Pricing, UpdatePriceRequest } from '@hjc/masterdata-types';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PricingService {
  private http = inject(HttpClient);

  measures = [
    { id: '€/tn', name: '€/tn' },
    { id: '€/pc', name: '€/pc' },
    { id: '€/1000l', name: '€/1000l' },
    { id: '€/km', name: '€/km' },
    { id: '€/h', name: '€/h' },
    { id: '%', name: '%' }
  ];

  /**
   * Updates price entries with new rates
   * @param prices Array of price IDs to update
   * @param changeFactor The factor to apply (percentage or absolute value)
   * @param changeType Whether the change is percentage-based or absolute
   * @param validFrom When the new prices should take effect
   * @returns Updated price data
   */
  async updatePrices(
    prices: string[],
    changeFactor: number,
    changeType: 'percentage' | 'absolute',
    validFrom: Date | string
  ): Promise<ApiResponse<{ updatedPrices: Pricing[] }>> {
    if (!prices.length) return { data: { updatedPrices: [] } };

    const validFromString = validFrom instanceof Date ? validFrom.toISOString() : validFrom;

    const requestData: BulkUpdateRatesRequest = {
      ids: prices,
      amount: changeFactor,
      updateType: changeType === 'percentage' ? 'relative' : 'absolute',
      validFrom: validFromString
    };

    return firstValueFrom(this.http.post<ApiResponse<{ updatedPrices: Pricing[] }>>('/api/prices/bulk-update', requestData));
  }

  async addPrice(data: CreatePriceRequest) {
    return firstValueFrom(this.http.post<ApiResponse<Pricing>>(`/api/prices`, data));
  }

  async getPriceById(id: string) {
    return firstValueFrom(this.http.get<ApiResponse<Pricing>>(`/api/prices/${id}`));
  }

  async updatePrice(data: UpdatePriceRequest, id: string) {
    return firstValueFrom(this.http.put<ApiResponse<Pricing>>(`/api/prices/${id}`, data));
  }

  async getPricingForSlaughterhouse(id: string) {
    return firstValueFrom(this.http.get<ApiResponse<Pricing[]>>(`/api/prices?category=slaughterhouse&categoryId=${id}`));
  }

  async getPricingForHaulier(id: string) {
    return firstValueFrom(this.http.get<ApiResponse<Pricing[]>>(`/api/prices?category=haulier&categoryId=${id}`));
  }

  async getPricingForByproduct(id: string) {
    return firstValueFrom(this.http.get<ApiResponse<Pricing[]>>(`/api/prices?category=byproduct&categoryId=${id}`));
  }

  async getPricingForService(id: string) {
    return firstValueFrom(this.http.get<ApiResponse<Pricing[]>>(`/api/prices?category=serviceproduct&categoryId=${id}`));
  }
}
